import { useState, useCallback } from 'react';
import { useAuth } from './useAuth';
import { useMCP } from './useMCP';
import type { Message, DateRange } from '../contexts/ConversationContext';
import { extractMCPData } from '../lib/mcp-client';
import type { A2UIResponse } from '../types/a2ui';

interface MCPToolCall {
  tool: string;
  arguments: Record<string, unknown>;
}

interface LLMResponse {
  content: string;
  /** A2UI declarative UI response */
  a2ui?: A2UIResponse;
  /** Legacy HTML visualization with data injected */
  html?: string;
  /** Data from MCP tool calls, keyed by tool name */
  data?: Record<string, unknown>;
  toolCalls?: MCPToolCall[];
}

interface UseLLMResult {
  generate: (prompt: string, conversationHistory: Message[], dateRange?: DateRange) => Promise<LLMResponse>;
  isLoading: boolean;
  error: string | null;
}

/**
 * Inject data into HTML by adding a script that sets window.DATA
 */
function injectDataIntoHTML(html: string, data: Record<string, unknown>): string {
  const dataScript = `<script>window.DATA = ${JSON.stringify(data)};</script>`;
  // Insert before </head> or at the start of <body>
  if (html.includes('</head>')) {
    return html.replace('</head>', `${dataScript}</head>`);
  } else if (html.includes('<body>')) {
    return html.replace('<body>', `<body>${dataScript}`);
  }
  // Fallback: prepend to HTML
  return dataScript + html;
}

export function useLLM(): UseLLMResult {
  const { accessToken } = useAuth();
  const mcp = useMCP();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generate = useCallback(
    async (prompt: string, conversationHistory: Message[], dateRange?: DateRange): Promise<LLMResponse> => {
      console.log('[useLLM] generate called, accessToken:', accessToken ? 'present' : 'missing');

      if (!accessToken) {
        throw new Error('Not authenticated');
      }

      setIsLoading(true);
      setError(null);

      try {
        // Get schema context from MCP
        let schemaContext = '';
        if (mcp) {
          try {
            console.log('[useLLM] Getting schema context...');
            schemaContext = await mcp.getSchemaContext();
            console.log('[useLLM] Schema context:', schemaContext.substring(0, 100) + '...');
          } catch (e) {
            console.warn('[useLLM] Failed to get schema context:', e);
          }
        }

        // Call LLM endpoint
        console.log('[useLLM] Calling /api/llm/generate...');
        const response = await fetch('/api/llm/generate', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            prompt,
            conversationHistory: conversationHistory.map((m) => ({
              role: m.role,
              content: m.content,
            })),
            schemaContext,
            dateRange: dateRange?.preset !== 'all' ? dateRange : undefined,
          }),
        });

        console.log('[useLLM] Response status:', response.status);

        if (!response.ok) {
          const errorText = await response.text();
          console.error('[useLLM] Error response:', errorText);
          let errorData: { error?: string } = {};
          try {
            errorData = JSON.parse(errorText);
          } catch {
            // Not JSON
          }
          throw new Error(errorData.error || `Request failed: ${response.status} - ${errorText.substring(0, 200)}`);
        }

        const responseData = (await response.json()) as { content: string; a2ui?: A2UIResponse; html?: string; toolCalls?: MCPToolCall[] };
        console.log('[useLLM] Response data:', responseData);
        console.log('[useLLM] a2ui present:', !!responseData.a2ui);
        console.log('[useLLM] html present:', !!responseData.html);
        console.log('[useLLM] toolCalls present:', !!responseData.toolCalls);
        if (responseData.a2ui) {
          console.log('[useLLM] a2ui components:', responseData.a2ui.components?.length);
        }
        if (responseData.html) {
          console.log('[useLLM] html length:', responseData.html.length);
        }

        // Execute MCP tool calls if present and merge results into data object
        const data: Record<string, unknown> = {};

        if (responseData.toolCalls && responseData.toolCalls.length > 0 && mcp) {
          // Filter out execute_graphql - LLM generates invalid queries
          const safeCalls = responseData.toolCalls.filter(tc => {
            if (tc.tool === 'execute_graphql') {
              console.log('[useLLM] Skipping execute_graphql - using simple tools instead');
              return false;
            }
            return true;
          });

          console.log('[useLLM] Executing', safeCalls.length, 'tool calls...');

          for (const toolCall of safeCalls) {
            try {
              console.log('[useLLM] Calling tool:', toolCall.tool, toolCall.arguments);
              const result = await mcp.callTool(toolCall.tool, toolCall.arguments);
              const extracted = extractMCPData(result);
              // Store result under tool name
              data[toolCall.tool] = extracted;
              console.log('[useLLM] Tool result:', toolCall.tool, typeof extracted === 'string' ? extracted.substring(0, 200) : extracted);
            } catch (e) {
              console.error(`[useLLM] Tool call failed: ${toolCall.tool}`, e);
            }
          }
        }

        // A2UI: Data is bound at render time, no need for follow-up calls
        // The data object contains all MCP tool results keyed by tool name
        // e.g., data.query_things = { items: [...], totalCount: N }

        // Legacy HTML: If we have HTML and data, inject the data into the HTML
        let finalHtml = responseData.html;
        if (finalHtml && Object.keys(data).length > 0) {
          // Merge all tool results into a single DATA object
          // Use the first tool's result as the main data (usually query_things, etc.)
          const mainData = Object.values(data)[0] || {};
          console.log('[useLLM] Injecting data into HTML');
          finalHtml = injectDataIntoHTML(finalHtml, mainData as Record<string, unknown>);
        }

        // Log what we're returning
        console.log('[useLLM] Returning response with:', {
          hasA2ui: !!responseData.a2ui,
          hasHtml: !!finalHtml,
          dataKeys: Object.keys(data),
          toolCallCount: responseData.toolCalls?.length || 0,
        });

        return {
          content: responseData.content,
          a2ui: responseData.a2ui,
          html: finalHtml,
          data: Object.keys(data).length > 0 ? data : undefined,
          toolCalls: responseData.toolCalls,
        };
      } catch (e) {
        const errorMessage = e instanceof Error ? e.message : 'Generation failed';
        setError(errorMessage);
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    [accessToken, mcp]
  );

  return { generate, isLoading, error };
}
